import { usePayment } from "../hooks";
import {
  Container,
  Loading,
  PaymentActions,
  PaymentForm,
  PaymentHeader,
  PaymentMethods,
  PaymentSummary,
  PhoneNumberInputs,
} from "../components";
import StepContainer from "../components/StepContainer";
import MerchantInfo from "../components/merchantInfo";
import { ThemeProps } from "../models/theme";
import { Box, Typography, Paper, Alert, AlertTitle } from "@mui/material";
import { useQueryPaymentMethods } from "../api/queries";
import { getStorageToken } from "../utils";
import { PaymentMethod } from "../models/payment";
import { useNavigate } from "react-router-dom";
import { CorporateService } from "../models/Service";
import { useTranslation } from "react-i18next";
import SearchCustomerForm from "../components/SearchCustomerForm";
import PaymentForSearch from "../components/paymentForSearch";
import { FormProvider, useForm } from "react-hook-form";
import ServiceChargeSummary from "../components/ServiceChargeSummary";

const PaymentPage = ({
  service,
  theme,
  corporateId,
}: {
  service: CorporateService;
  theme: ThemeProps;
  corporateId: string;
}) => {
  const methods = useForm();
  const { t } = useTranslation();
  const { currentStep, error } = usePayment();
  const token = getStorageToken();
  const navigate = useNavigate();
  const paymentMethods = useQueryPaymentMethods({
    corporateId,
    enabled: !!token,
    authorisation: token || "",
  });

  if (paymentMethods?.isLoading) return <Loading />;

  if (paymentMethods?.isError) {
    navigate(-1);
    return null;
  }

  return (
    <Container
      justifyContent="center"
      alignItems="center"
      height="70vh"
      sx={{ borderColor: theme?.color_primary }}
    >
      <PaymentHeader
        isCustomerSearchEnabled={service?.is_customer_search_enabled}
      />
      <FormProvider {...methods}>
        <form onSubmit={() => null}>
          <Box>
            {currentStep === 1 && (
              <StepContainer
                title={t("payment.step1_title")}
                description={
                  service?.is_customer_search_enabled
                    ? t("payment.step0_description")
                    : t("payment.step1_description")
                }
              />
            )}

            {currentStep === 2 && (
              <StepContainer
                title={t("payment.step2_title")}
                description={
                  service?.is_customer_search_enabled
                    ? t("payment.step1_description")
                    : t("payment.step1_description")
                }
              />
            )}

            {currentStep === 3 && (
              <StepContainer
                title={t("payment.step3_title")}
                description={
                  service?.is_customer_search_enabled
                    ? t("payment.step2_description")
                    : t("payment.step2_description")
                }
              />
            )}
            {error && (
              <Alert severity="error" sx={{ margin: 5 }}>
                <AlertTitle>{t("error")}</AlertTitle>
                {error}
              </Alert>
            )}

            <MerchantInfo
              merchantName={service?.service_name}
              logoSrc={service?.logo.url}
              borderColor={theme?.color_primary}
            />

            <Box
              sx={{
                border: 1,
                borderBottom: currentStep !== 1 ? "none" : undefined,
                borderColor: theme?.color_primary,
                borderTopColor: "none",
                borderRadius: 1,
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                borderBottomRightRadius: currentStep !== 1 ? 0 : 1,
                borderBottomLeftRadius: currentStep !== 1 ? 0 : 1,
                px: { xs: 2, md: 8 },
                py: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: theme.light_gray,
              }}
            >
              {service?.is_customer_search_enabled ? (
                <Paper
                  elevation={1}
                  sx={{
                    p: 4,
                    width: "100%",
                    maxWidth: currentStep === 2 ? undefined : 800,
                    boxShadow: "none",
                    bgcolor: theme.light_gray,
                  }}
                >
                  {currentStep === 1 && (
                    <SearchCustomerForm corporateId={corporateId} />
                  )}
                  {(currentStep === 2 || currentStep === 3) && (
                    <PaymentForSearch
                      service={service}
                      payments={paymentMethods?.data as PaymentMethod[]}
                    />
                  )}
                </Paper>
              ) : (
                <Paper
                  elevation={2}
                  sx={{ p: 4, width: "100%", maxWidth: 800 }}
                >
                  {currentStep === 1 ? (
                    <>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {t("payment.details_title")}
                      </Typography>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        gutterBottom
                      >
                        {t("payment.details_description")}
                      </Typography>
                      <PaymentForm />
                      <PaymentMethods
                        payments={paymentMethods?.data as PaymentMethod[]}
                      />
                      <PhoneNumberInputs service={service} />
                    </>
                  ) : (
                    <>
                      <PaymentSummary />
                      <ServiceChargeSummary />
                    </>
                  )}
                </Paper>
              )}
            </Box>

            {(!service?.is_customer_search_enabled || currentStep !== 1) && (
              <PaymentActions service={service} />
            )}
          </Box>
        </form>
      </FormProvider>
    </Container>
  );
};

export default PaymentPage;
